import { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { SITE, PEST_OPTIONS } from "../config/site";
import { useLead } from "../context/LeadContext";
import { CheckCircle2, Phone, MessageCircle, Loader2, ArrowLeft, ArrowRight, CalendarDays } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-transparent border-b border-border focus:border-wine outline-none py-3 text-base text-charcoal placeholder:text-stone2/70 transition-colors duration-300 rounded-none";

const Field = ({ label, children, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="block text-[11px] font-bold tracking-[0.2em] uppercase text-stone2 mb-1">{label}</span>
    {children}
  </label>
);

const getDays = () => {
  const out = [];
  const now = new Date();
  for (let i = 0; i < SITE.booking.daysAhead; i++) {
    const d = new Date(now);
    d.setDate(now.getDate() + i);
    out.push(d);
  }
  return out;
};

const dayLabel = (d) => d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });

const STEPS = ["Date", "Time", "Details"];

export const LeadModal = () => {
  const { open, setOpen, prefillPest } = useLead();
  const [step, setStep] = useState(0);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", location: "Delhi", pest: "Cockroaches", email: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const days = getDays();

  useEffect(() => {
    if (open) {
      setStep(0);
      setDate(null);
      setTime(null);
      setStatus("idle");
      setError("");
      if (prefillPest) setForm((f) => ({ ...f, pest: prefillPest }));
    }
  }, [open, prefillPest]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\d{10}$/.test(form.phone.replace(/\D/g, "").slice(-10))) {
      setError("Please enter a valid 10-digit mobile number.");
      return;
    }
    setStatus("loading");
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        callback_time: `${dayLabel(date)} · ${time}`,
        booking_date: date.toISOString().slice(0, 10),
        booking_time: time,
      });
      setStatus("done");
    } catch (err) {
      setStatus("idle");
      setError("Something went wrong. Please try again or call us directly.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="lead-modal" className="bg-ivory border border-border rounded-none max-w-lg p-0 gap-0 [&>button]:text-charcoal">
        {status === "done" ? (
          <div className="p-10 text-center" data-testid="booking-success">
            <CheckCircle2 className="h-12 w-12 text-wine mx-auto mb-5" strokeWidth={1.5} />
            <h3 className="font-display text-3xl font-semibold text-charcoal mb-3">Your free inspection has been booked.</h3>
            <p className="text-stone2 text-sm leading-relaxed">
              {dayLabel(date)} · {time} — {form.location}. Our team will call {form.phone} to confirm.
            </p>
            <p className="text-charcoal text-sm font-semibold mt-6">
              Need help sooner? Call us at{" "}
              <a data-testid="success-phone-link" href={SITE.phoneHref} className="text-wine underline underline-offset-4">{SITE.phoneDisplay}</a>.
            </p>
            <div className="flex flex-col gap-3 mt-6">
              <a data-testid="success-call-btn" href={SITE.phoneHref} className="inline-flex items-center justify-center gap-2 bg-wine text-ivory px-6 py-3.5 text-[13px] font-bold tracking-[0.12em] uppercase hover:bg-wine-light transition-colors">
                <Phone className="h-4 w-4" /> Call Now
              </a>
              <a data-testid="success-whatsapp-btn" href={SITE.whatsappHref} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#1FA855] text-ivory px-6 py-3.5 text-[13px] font-bold tracking-[0.12em] uppercase hover:bg-[#23c05f] transition-colors shadow-[0_0_22px_rgba(31,168,85,0.4)]">
                <MessageCircle className="h-4 w-4" /> WhatsApp Us
              </a>
            </div>
          </div>
        ) : (
          <>
            <DialogHeader className="p-8 pb-0 text-left">
              <span className="text-[11px] tracking-[0.3em] uppercase font-bold text-gold mb-2">Free Inspection</span>
              <DialogTitle className="font-display text-3xl font-semibold text-charcoal">Book Your Free Inspection</DialogTitle>
              <DialogDescription className="text-stone2 text-sm">
                Choose a date and time — appointments available all days across Delhi NCR.
              </DialogDescription>
              <div className="flex gap-5 pt-4" data-testid="booking-steps">
                {STEPS.map((s, i) => (
                  <span key={s} className={`text-[10px] tracking-[0.22em] uppercase font-bold pb-1.5 border-b-2 ${i === step ? "text-wine border-wine" : i < step ? "text-gold border-gold" : "text-stone2/60 border-transparent"}`}>
                    0{i + 1} {s}
                  </span>
                ))}
              </div>
            </DialogHeader>

            {step === 0 && (
              <div className="p-8 pt-6" data-testid="booking-step-date">
                <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-stone2 mb-4 flex items-center gap-2">
                  <CalendarDays className="h-3.5 w-3.5 text-wine" /> Choose a date
                </p>
                <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 max-h-64 overflow-y-auto pr-1">
                  {days.map((d, i) => {
                    const selected = date && d.toDateString() === date.toDateString();
                    return (
                      <button
                        key={i}
                        type="button"
                        data-testid={`booking-date-${i}`}
                        onClick={() => setDate(d)}
                        className={`border px-1 py-3 text-center transition-colors duration-200 ${selected ? "bg-wine text-ivory border-wine" : "border-border hover:border-wine text-charcoal"}`}
                      >
                        <span className={`block text-[9px] tracking-[0.18em] uppercase font-bold ${selected ? "text-gold" : "text-stone2"}`}>
                          {i === 0 ? "Today" : d.toLocaleDateString("en-IN", { weekday: "short" })}
                        </span>
                        <span className="block font-display text-2xl font-semibold leading-tight">{d.getDate()}</span>
                        <span className={`block text-[10px] ${selected ? "text-ivory/70" : "text-stone2"}`}>{d.toLocaleDateString("en-IN", { month: "short" })}</span>
                      </button>
                    );
                  })}
                </div>
                <button
                  data-testid="booking-date-continue"
                  disabled={!date}
                  onClick={() => setStep(1)}
                  className="w-full mt-6 bg-wine text-ivory py-4 text-[13px] font-bold tracking-[0.14em] uppercase hover:bg-wine-light transition-colors duration-300 disabled:opacity-40 inline-flex items-center justify-center gap-2"
                >
                  Choose Time <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            )}

            {step === 1 && (
              <div className="p-8 pt-6" data-testid="booking-step-time">
                <button data-testid="booking-back-date" onClick={() => setStep(0)} className="inline-flex items-center gap-1.5 text-[11px] font-bold tracking-[0.18em] uppercase text-stone2 hover:text-wine transition-colors mb-5">
                  <ArrowLeft className="h-3.5 w-3.5" /> {dayLabel(date)}
                </button>
                <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-stone2 mb-4">Available time slots</p>
                <div className="grid grid-cols-3 gap-2 max-h-64 overflow-y-auto pr-1">
                  {SITE.booking.slots.map((s, i) => (
                    <button
                      key={s}
                      type="button"
                      data-testid={`booking-time-${i}`}
                      onClick={() => setTime(s)}
                      className={`border py-3 text-[13px] font-semibold transition-colors duration-200 ${time === s ? "bg-wine text-ivory border-wine" : "border-border hover:border-wine text-charcoal"}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
                <button
                  data-testid="booking-time-continue"
                  disabled={!time}
                  onClick={() => setStep(2)}
                  className="w-full mt-6 bg-wine text-ivory py-4 text-[13px] font-bold tracking-[0.14em] uppercase hover:bg-wine-light transition-colors duration-300 disabled:opacity-40 inline-flex items-center justify-center gap-2"
                >
                  Your Details <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            )}

            {step === 2 && (
              <form onSubmit={submit} className="p-8 pt-6 space-y-5" data-testid="booking-form">
                <button type="button" data-testid="booking-back-time" onClick={() => setStep(1)} className="inline-flex items-center gap-1.5 text-[11px] font-bold tracking-[0.18em] uppercase text-stone2 hover:text-wine transition-colors">
                  <ArrowLeft className="h-3.5 w-3.5" /> {dayLabel(date)} · {time}
                </button>
                <Field label="Name" testid="field-name">
                  <input data-testid="booking-name-input" required value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} />
                </Field>
                <Field label="Phone Number (Required)" testid="field-phone">
                  <input data-testid="booking-phone-input" required type="tel" value={form.phone} onChange={set("phone")} placeholder="10-digit mobile number" className={inputCls} />
                </Field>
                <div className="grid grid-cols-2 gap-5">
                  <Field label="Location" testid="field-location">
                    <select data-testid="booking-location-select" value={form.location} onChange={set("location")} className={inputCls}>
                      {[...SITE.primaryAreas, ...SITE.extraAreas, "Other"].map((a) => (
                        <option key={a}>{a}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Pest / Problem" testid="field-pest">
                    <select data-testid="booking-pest-select" value={form.pest} onChange={set("pest")} className={inputCls}>
                      {PEST_OPTIONS.map((p) => (
                        <option key={p}>{p}</option>
                      ))}
                    </select>
                  </Field>
                </div>
                <Field label="Email (Optional)" testid="field-email">
                  <input data-testid="booking-email-input" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
                </Field>
                {error && <p data-testid="booking-form-error" className="text-wine text-sm">{error}</p>}
                <button
                  data-testid="booking-confirm-btn"
                  type="submit"
                  disabled={status === "loading"}
                  className="w-full bg-wine text-ivory py-4 text-[13px] font-bold tracking-[0.14em] uppercase hover:bg-wine-light transition-colors duration-300 disabled:opacity-60 inline-flex items-center justify-center gap-2"
                >
                  {status === "loading" && <Loader2 className="h-4 w-4 animate-spin" />}
                  Confirm Appointment
                </button>
                <p className="text-[11px] text-stone2 text-center">Your phone number is required so our team can confirm your inspection.</p>
              </form>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
